/* @flow */

import * as React from 'react';
import {ContactIcon, AccountIcon, AccountsCircleIcon, ContactsCircleIcon} from '../icon';
import {colors} from '../colors';
import {LeadAvatar} from './lead-avatar';

import './avatar.css';

export const DEFAULT_SIZE = 30;

type Props = {|
    avatarUrl: ?string,
    circular: boolean,
    // border adds a 2 px solid border to the avatar.
    border?: boolean,
    initials?: ?string,
    size: number,
    type?: string,
    /** Leads should also provide their status, for the alt tag */
    status?: string,
|};

/**
 * A simple graphical representation of a company, person, lead, user or team.
 */
export class Avatar extends React.PureComponent<Props> {
    static defaultProps = {
        circular: true,
        size: DEFAULT_SIZE,
    };

    render() {
        // Guard against missing or empty url AND initials (we need at least one of them)
        const needsPlaceholder =
            !this.props.avatarUrl &&
            !this.props.initials &&
            !['contacts', 'accounts'].includes(this.props.type); // contacts and accounts (including mcfxCompanies) have icons to fallback on

        if (needsPlaceholder) {
            // Provide a placeholder div, to prevent other elements from shifting around
            return <div style={{height: this.props.size, width: this.props.size}} />;
        }

        if (this.props.type === 'leads' || this.props.type === 'leadStatus') {
            return (
                <LeadAvatar
                    avatarUrl={this.props.avatarUrl}
                    size={this.props.size}
                    status={this.props.status}
                />
            );
        }

        let style: Object = {
            width: `${this.props.size}px`,
            height: `${this.props.size}px`,
            lineHeight: `${this.props.size}px`,
            fontSize: Math.floor(this.props.size / 2 - this.props.size / 15),
        };

        if (!this.props.initials) style = Object.assign(style, {backgroundColor: 'transparent'});
        if (this.props.circular)
            style = Object.assign(style, {borderRadius: `${this.props.size}px`});

        const typeClassName = this.props.type ? `type-${this.props.type}` : '';

        return (
            <div
                styleName={`${
                    this.props.border ? 'container--border' : 'container'
                } ${typeClassName}`}
                style={style}
            >
                {this.renderAvatarContents(style)}
            </div>
        );
    }

    // This sucks, but will get fixed once we have a more consistent set of icons, and leads have avatarUrls
    renderAvatarContents(style: Object) {
        let initials, avatarImg, entityIcon;

        if (this.props.initials) {
            initials = (
                <div style={style} styleName='initials'>
                    {this.props.initials}
                </div>
            );
        }

        if (this.props.avatarUrl) {
            avatarImg = (
                <img
                    style={style}
                    alt='avatar'
                    src={`${this.props.avatarUrl}?width=100&height=100`}
                />
            );
        }

        // Users don't have SVG entity icons… yet
        if (['contacts', 'accounts'].includes(this.props.type) && !this.props.initials) {
            if (this.props.type === 'contacts') {
                entityIcon = this.props.circular ? (
                    <ContactsCircleIcon size={this.props.size} fill={colors.navyDk} />
                ) : (
                    <ContactIcon size={this.props.size} />
                );
            } else {
                entityIcon = this.props.circular ? (
                    <AccountsCircleIcon size={this.props.size} fill={colors.plumDk} />
                ) : (
                    <AccountIcon size={this.props.size} />
                );
            }
        }

        if (!initials && !avatarImg && !entityIcon) return null;

        return (
            <>
                {avatarImg ? avatarImg : undefined}
                {initials ? initials : entityIcon}
            </>
        );
    }
}
